// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getFirstElement } from 'clark-utils';

import {
  incrementPayerPage,
  getPayerAuthenticated,
  postPayerPaymentMethod,
  postPayerChargeConfirmation,
} from 'actions/payer';

import Splash from 'components/payer/splash';
import Charge from 'components/payer/charge';
import Success from 'components/payer/success';
import CanceledInvoice from 'components/payer/canceled';
import Loader from 'components/loader';

import { type StateType as ReducerStateType } from 'reducers';

import { confirmChargeWithNewPaymentMethod, needsToSetACreditCard } from './helpers';

const COMPONENTS = [Splash, Charge, Success];

type PropsFromStateType = {
  page: number,
  payer: *,
  charge: *,
  payerCharge: *,
  business: *,
  paymentMethod: *,
  client: *,
  isLoading: number,
  query: string,
  payerPath: string,
};

const mapStateToProps = ({
  payer,
  form,
  router,
  application,
}: ReducerStateType): PropsFromStateType => {
  const { page, data, charge, client, business, paymentMethod } = payer;
  const { payerCharge } = form;
  const { isLoading } = application;

  return {
    page,
    charge,
    payerCharge,
    business,
    paymentMethod,
    client,
    isLoading,
    payer: getFirstElement(data), // only returns one record
    query: router.location.search,
    payerPath: router.location.pathname,
  };
};

type DispatchPropsType = {
  dispatchIncrementPayerPage: typeof incrementPayerPage,
  dispatchGetPayerAuthenticated: typeof getPayerAuthenticated,
  dispatchPostPayerPaymentMethod: typeof postPayerPaymentMethod,
  dispatchPostPayerChargeConfirmation: typeof postPayerChargeConfirmation,
};

const mapDispatchToProps: DispatchPropsType = {
  dispatchIncrementPayerPage: incrementPayerPage,
  dispatchGetPayerAuthenticated: getPayerAuthenticated,
  dispatchPostPayerPaymentMethod: postPayerPaymentMethod,
  dispatchPostPayerChargeConfirmation: postPayerChargeConfirmation,
};

type PropsType = DispatchPropsType & PropsFromStateType;

type StateType = {
  showNewCreditCardForm: boolean,
  pendingBankAccountAttributes: *,
};

class Payer extends Component<PropsType, StateType> {
  constructor(props) {
    super(props);

    this.state = {
      showNewCreditCardForm: false,
      pendingBankAccountAttributes: undefined,
    };
  }

  componentWillMount() {
    const { query, payerPath, dispatchGetPayerAuthenticated } = this.props;

    let pathToken;
    let queryToken;

    try {
      pathToken = payerPath.split('/payment/')[1].replace(/^tok_/, '');
    } catch (e) {
      // eslint-disable-next-line
      console.log(e);
    }

    try {
      queryToken = query.slice(18);
    } catch (e) {
      // eslint-disable-next-line
      console.log(e);
    }

    dispatchGetPayerAuthenticated(pathToken || queryToken);
  }

  handleClick = stripe =>
    // why does submit also cause the screen to flash?
    (async () => {
      const {
        page,
        dispatchIncrementPayerPage,
        payerCharge = {},
        charge,
        paymentMethod,
        dispatchPostPayerPaymentMethod,
        dispatchPostPayerChargeConfirmation,
      } = this.props;
      const { pendingBankAccountAttributes: bankAttributes } = this.state;

      const {
        id: chargeId,
        attributes: { paymentMethodType: paymentMethodToUse },
      } = charge;
      const { values: formValues } = payerCharge;

      const isUserOnChargeScreen = page === 1;
      const isUserSettingNewPaymentMethod =
        this.showNewCreditCardForm(paymentMethod, paymentMethodToUse) || bankAttributes;

      if (isUserOnChargeScreen) {
        const data = { chargeId, ...formValues };

        if (isUserSettingNewPaymentMethod) {
          confirmChargeWithNewPaymentMethod(
            data,
            charge,
            stripe,
            bankAttributes,
            dispatchPostPayerPaymentMethod,
          );
        } else {
          dispatchPostPayerChargeConfirmation({ chargeId });
        }
      } else {
        dispatchIncrementPayerPage();
      }
    })();

  handlePlaidSuccess = (bankAccountPublicToken, metadata) => {
    const {
      account: {
        id: bankAccountId,
        mask: bankAccountLast4,
        name: bankName,
        subtype: bankAccountType,
      },
    } = metadata;

    this.setState({
      pendingBankAccountAttributes: {
        bankAccountPublicToken,
        bankAccountId,
        bankAccountLast4,
        bankAccountType,
        bankName,
      },
    });
  };

  showNewCreditCardForm = (paymentMethod, paymentMethodToUse) =>
    // eslint-disable-next-line react/destructuring-assignment
    needsToSetACreditCard(paymentMethod, paymentMethodToUse) || this.state.showNewCreditCardForm;

  toggleCreditCardForm = () => {
    this.setState(({ showNewCreditCardForm }) => ({
      showNewCreditCardForm: !showNewCreditCardForm,
    }));
  };

  render() {
    const { page, payer, business, charge, client, isLoading, paymentMethod } = this.props;
    const { pendingBankAccountAttributes } = this.state;
    const Container = COMPONENTS[page];

    const {
      attributes: { logoUrl },
    } = business;
    const {
      attributes: { status, paymentMethodType: paymentMethodToUse },
    } = charge;

    if (status === 'canceled') {
      return <CanceledInvoice />;
    }

    return isLoading ? (
      <Loader />
    ) : (
      <Container
        handleClick={this.handleClick}
        handlePlaidSuccess={this.handlePlaidSuccess}
        business={business}
        payer={payer}
        client={client}
        charge={charge}
        logoUrl={logoUrl}
        showNewCreditCardForm={this.showNewCreditCardForm(paymentMethod, paymentMethodToUse)}
        toggleCreditCardForm={this.toggleCreditCardForm}
        pendingBankAccountAttributes={pendingBankAccountAttributes}
      />
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Payer);



// WEBPACK FOOTER //
// ./src/screens/payer/index.js